import { css } from 'lit';
import { scrollbarStyles } from '../../styles/scrollbars.css.mjs'

export default css`
  :host {
    display: block;
    font-family: var(--font-family-base, 'Inter, Arial, sans-serif');
    position: relative;
    --select-width: stretch;
    --select-border: var(--sm-line) solid var(--border-strong);
    --select-bg: var(--surface-raised);
    --select-icon-color: var(--color-dark-gray);
    --select-icon-color-disabled: var(--color-gray-3);
    --menu-bg: var(--surface-raised);
    --menu-shadow: 0px 6px 14px -6px rgba(19, 25, 39, 0.12), 0px 10px 32px -4px rgba(19, 25, 39, 0.1);
    --menu-border-radius: var(--radius-sm);
    --checkbox-bg-selected: var(--color-secondary);
  }

  .select-wrapper {
    width: var(--select-width, stretch);
    width: -moz-available;
    width: -webkit-fill-available;
    flex-direction: column;
    align-items: stretch;
  }

  .trigger {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-sm);
    gap: var(--spacing-sm);
    width: inherit;
    height: auto;
    border: var(--select-border);
    border-radius: var(--radius-md);
    background: var(--select-bg);
    cursor: pointer;
    transition: all var(--duration-base, 0.2s) var(--easing-ease, ease);
  }

  :host([height="tiny"]) .trigger {
    height: var(--height-select-tiny);
  }

  :host([height="small"]) .trigger {
    height: var(--height-select-small);
  }

  :host([height="medium"]) .trigger {
    height: var(--height-select-medium);
  }

  :host([height="large"]) .trigger {
    height: var(--height-select-large);
  }

  :host([height="giant"]) .trigger {
    height: var(--height-select-giant);
  }

  .trigger:hover:not(.disabled):not(.expanded),
  .trigger.focus:not(.disabled):not(.expanded) {
    border-color: var(--color-secondary);
  }

  .trigger.expanded {
    border-color: var(--color-secondary);
    box-shadow: var(--shadow-focus);
  }

  .trigger:focus-visible {
    outline: 2px solid var(--color-secondary);
    outline-offset: 2px;
  }

  .trigger.disabled {
    border-color: var(--border-subtle);
    cursor: not-allowed;
    opacity: 0.7;
  }

  .icon, ::slotted([slot="trigger-icon"]), ::slotted([slot^="option-icon-"]) {
    width: 24px;
    height: 24px;
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--select-icon-color);
  }

  ::slotted([slot^="option-icon-"]) {
    width: 16px;
    height: 16px;
  }

  .text, ::slotted([slot="trigger-text"]) {
    flex: 1;
    font-size: var(--font-size-b1);
    line-height: var(--line-height-b1);
    font-weight: var(--font-weight-regular);
    color: var(--color-dark-gray);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 30ch;
  }

  .trigger.disabled .text,
  .trigger.disabled ::slotted([slot="trigger-text"]),
  .trigger.disabled .icon,
  .trigger.disabled ::slotted([slot="trigger-icon"]) {
    color: var(--select-icon-color-disabled);
  }

  .arrow, ::slotted([slot="arrow-icon"]) {
    width: 18px;
    height: 18px;
    color: var(--select-icon-color);
  }

  .trigger.disabled .arrow,
  .trigger.disabled ::slotted([slot="arrow-icon"]) {
    color: var(--select-icon-color-disabled);
  }

  .trigger.expanded .arrow,
  .trigger.expanded ::slotted([slot="arrow-icon"]) {
    color: var(--color-secondary);
  }

  .menu {
    display: flex;
    position: absolute;
    flex-direction: column;
    align-items: flex-start;
    padding: var(--spacing-x) 0;
    max-height: 288px;
    width: stretch;
    width: -moz-available;
    width: -webkit-fill-available;
    overflow-y: auto;
    background: var(--menu-bg);
    box-shadow: var(--menu-shadow);
    border-radius: var(--menu-border-radius);
    z-index: var(--z-index-menu);
    box-sizing: border-box;
    overscroll-behavior: contain; 
  }

  .menu-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: var(--spacing-sm) var(--spacing-md);
    gap: var(--spacing-sm);
    width: stretch;
    width: -moz-available;
    width: -webkit-fill-available;
    height: auto;
    cursor: pointer;
    transition: background var(--duration-base) var(--easing-ease, ease);
    box-sizing: border-box;
  }

  .menu-item:hover:not(.selected),
  .menu-item:focus:not(.selected) {
    background: var(--surface-sunken);
  }

  .menu-item.selected {
    background: var(--menu-bg);
  }

  .option-text, ::slotted([slot^="option-text-"]) {
    flex: 1;
    font-size: var(--font-size-b1);
    line-height: var(--line-height-b1);
    font-weight: var(--font-weight-regular);
    color: var(--color-dark-gray);
  }

  .checkbox, ::slotted([slot^="option-checkbox-"]) {
    width: 24px;
    height: 24px;
    border: var(--sm-line) solid var(--color-primary-dark);
    border-radius: var(--radius-s);
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .checkbox.checked, ::slotted([slot^="option-checkbox-"].checked) {
    border-color: transparent;
    background: var(--checkbox-bg-selected);
  }

  .menu-item:hover:not(.selected) .checkbox,
  .menu-item:focus:not(.selected) .checkbox,
  .menu-item:hover:not(.selected) ::slotted([slot^="option-checkbox-"]),
  .menu-item:focus:not(.selected) ::slotted([slot^="option-checkbox-"]) {
    border-color: var(--color-secondary);
  }

  .icon.placeholder, ::slotted([slot="trigger-icon"].placeholder) {
    visibility: hidden;
  }

  @media (max-width: 768px) {
    .select-wrapper {
      width: -moz-available;
      width: -webkit-fill-available;
      width: stretch;
    }
    .menu {
      width: -moz-available;
      width: -webkit-fill-available;
      width: stretch;
    }
  }
`;